/** True when a purchase summary row is linked to a purchase order. */
function purchaseNoIndicatesWithOrder(row: Record<string, unknown>): boolean | null {
  const purchaseNo = String(
    row.purchase_no ?? row.txt_prno ?? row.txt_purchaseno ?? ""
  )
    .trim()
    .toUpperCase();
  if (purchaseNo.startsWith("PURW_")) return false;
  if (purchaseNo.startsWith("PUR_")) return true;
  return null;
}

export function hasPurchaseOrderOnSummaryRow(
  row: Record<string, unknown>
): boolean {
  const byPurchaseNo = purchaseNoIndicatesWithOrder(row);
  if (byPurchaseNo === true) return true;
  if (byPurchaseNo === false) return false;

  const orderNo = row.purchase_order_no ?? row.txt_pr_ord_no;
  const orderId = row.purchase_order_id ?? row.drp_purchase;
  if (String(orderNo ?? "").trim()) return true;
  if (String(orderId ?? "").trim()) return true;
  return false;
}

/** Resolve read-only view target for purchase summary (screen 140 vs 139). */
export function resolvePurchaseSummaryViewTarget(row: Record<string, unknown>): {
  screenId: string;
  moduleName: string;
} {
  if (hasPurchaseOrderOnSummaryRow(row)) {
    return { screenId: "140", moduleName: "pur_add" };
  }
  return { screenId: "139", moduleName: "pur_without_order_add" };
}

function pickNonEmptyString(...values: unknown[]): string {
  for (const value of values) {
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      return String(value);
    }
  }
  return "";
}

/** Merge purchase-order lookup row into pur_add form (dropdown + BindParent fields). */
export function mergePurchaseOrderLookupIntoFormData(
  formData: Record<string, unknown>,
  lookupRow: Record<string, unknown>,
  purchaseOrderId?: string
): Record<string, unknown> {
  const idStr = pickNonEmptyString(
    purchaseOrderId,
    lookupRow.id,
    lookupRow.value,
    formData.drp_purchase,
    formData.purchase_order_id
  );
  if (!idStr) return lookupRow;

  const label =
    pickNonEmptyString(
      lookupRow.name,
      lookupRow.label,
      lookupRow.purchase_order_no,
      formData.purchase_order_id_desc
    ) || idStr;

  const selectedData: Record<string, unknown> = {
    ...lookupRow,
    id: idStr,
    value: idStr,
    label,
    name: label,
    sname: pickNonEmptyString(
      lookupRow.sname,
      lookupRow.suppliername,
      formData.sup_name
    ),
    taxregno: pickNonEmptyString(
      lookupRow.taxregno,
      formData.txt_trn,
      formData.vendor_taxregno
    ),
    billing_address: pickNonEmptyString(
      lookupRow.billing_address,
      formData.txt_billingaddress
    ),
    mobile_no: pickNonEmptyString(
      lookupRow.mobile_no,
      formData.txt_mobile,
      formData.mobile_no
    ),
    term: pickNonEmptyString(
      lookupRow.term,
      formData.txt_credit_days,
      formData.credit_days
    ),
  };

  formData.drp_purchase = idStr;
  formData.purchase_order_id = idStr;

  const supplierName = pickNonEmptyString(selectedData.sname, formData.sup_name);
  if (supplierName) {
    formData.sup_name = supplierName;
    selectedData.sname = supplierName;
  }

  const trn = pickNonEmptyString(selectedData.taxregno, formData.txt_trn);
  if (trn && !pickNonEmptyString(formData.txt_trn)) {
    formData.txt_trn = trn;
  }

  const address = pickNonEmptyString(
    selectedData.billing_address,
    formData.txt_billingaddress
  );
  if (address && !pickNonEmptyString(formData.txt_billingaddress)) {
    formData.txt_billingaddress = address;
  }

  const creditDays = pickNonEmptyString(
    selectedData.term,
    formData.txt_credit_days
  );
  if (creditDays && !pickNonEmptyString(formData.txt_credit_days)) {
    formData.txt_credit_days = creditDays;
  }

  formData.drp_purchase_selectedData = selectedData;
  return selectedData;
}

/** Prefill pur_add header BindParent fields when viewing/editing an existing purchase. */
export function applyPurchaseWithOrderViewHydration(
  formData: Record<string, unknown>,
  sources: {
    response?: Record<string, unknown>;
    fallback?: Record<string, unknown>;
  } = {}
): Record<string, unknown> | undefined {
  const response = sources.response ?? {};
  const fallback = sources.fallback ?? {};

  const purchaseOrderId = pickNonEmptyString(
    formData.drp_purchase,
    formData.purchase_order_id,
    response.purchase_order_id,
    fallback.purchase_order_id
  );
  if (!purchaseOrderId) return undefined;

  const lookupRow: Record<string, unknown> = {
    id: purchaseOrderId,
    name: pickNonEmptyString(
      response.purchase_order_id_desc,
      formData.purchase_order_id_desc,
      fallback.purchase_order_no,
      fallback.txt_pr_ord_no
    ),
    sname: pickNonEmptyString(
      response.suppliername,
      response.supplier_name,
      response.sname,
      fallback.suppliername,
      fallback.supplier,
      formData.sup_name
    ),
    taxregno: pickNonEmptyString(
      response.vendor_taxregno,
      formData.txt_trn,
      fallback.vendor_taxregno
    ),
    billing_address: pickNonEmptyString(
      response.billing_address,
      formData.txt_billingaddress,
      fallback.billing_address
    ),
    mobile_no: pickNonEmptyString(
      response.mobile_no,
      formData.txt_mobile,
      fallback.mobile_no
    ),
    term: pickNonEmptyString(
      response.credit_days,
      formData.txt_credit_days,
      fallback.credit_days
    ),
  };

  return mergePurchaseOrderLookupIntoFormData(
    formData,
    lookupRow,
    purchaseOrderId
  );
}

/** After purchase line items load in view/edit, expose job card for pending-parts lookup. */
export function applyPurchaseViewSubmoduleHydration(
  formData: Record<string, unknown>
): void {
  const purchaseRecordId = String(
    formData.id ?? formData.purchase_id ?? ""
  ).trim();

  const existingSelected = formData.job_card_id_selected;
  if (
    existingSelected !== undefined &&
    existingSelected !== null &&
    String(existingSelected).trim() !== "" &&
    (!purchaseRecordId ||
      String(existingSelected).trim() !== purchaseRecordId)
  ) {
    return;
  }

  if (
    existingSelected !== undefined &&
    existingSelected !== null &&
    purchaseRecordId &&
    String(existingSelected).trim() === purchaseRecordId
  ) {
    delete formData.job_card_id_selected;
  }

  const lines =
    (formData.jobcard_details as unknown[]) ??
    (formData.purchase_details as unknown[]) ??
    [];
  if (!Array.isArray(lines) || lines.length === 0) return;

  for (const row of lines) {
    if (!row || typeof row !== "object") continue;
    const record = row as Record<string, unknown>;
    const jobCardId = record.job_card_id_item ?? record.job_card_id;
    if (
      jobCardId !== undefined &&
      jobCardId !== null &&
      String(jobCardId).trim() !== "" &&
      (!purchaseRecordId || String(jobCardId).trim() !== purchaseRecordId)
    ) {
      formData.job_card_id_selected = jobCardId;
      break;
    }
  }
}

export function findPurchaseDetailsSubModule(
  subModules: Array<Record<string, unknown>>
): Record<string, unknown> | undefined {
  return subModules.find((sm) => {
    const name = String(sm?.name || sm?.Name || "").toLowerCase();
    const table = String(sm?.TableName || sm?.tableName || "").toLowerCase();
    return name === "purchase_details" || table === "purchase_details";
  });
}

/** Load saved purchase_details rows for without-order view (screen 139). */
export async function loadPurchaseWithoutOrderSubmoduleData(
  formData: Record<string, unknown>,
  options: {
    recordId: string;
    purchaseDetailsSubModule?: Record<string, unknown>;
    getSubModuleData: (moduleName: string, recordId: string) => Promise<any[]>;
    mapRows: (subModule: Record<string, unknown>, apiRows: any[]) => Record<string, any>[];
    assignRows: (
      formData: Record<string, any>,
      subModule: Record<string, unknown>,
      rows: Record<string, any>[],
      parentModuleName?: string
    ) => void;
    parentModuleName?: string;
  }
): Promise<boolean> {
  const recordId = String(options.recordId || "").trim();
  if (!recordId) {
    logPurchaseWithoutOrderSubmodule("load_skip_no_record_id");
    return false;
  }

  const subModule =
    options.purchaseDetailsSubModule ||
    ({ name: "purchase_details", BindValue: "jobcard_details", TableName: "purchase_details" } as Record<
      string,
      unknown
    >);

  const moduleName = String(
    subModule.TableName || subModule.tableName || subModule.name || subModule.Name || "purchase_details"
  ).trim();

  logPurchaseWithoutOrderSubmodule("load_fetch_start", {
    recordId,
    moduleName,
    bindValue: subModule.BindValue || subModule.bindValue,
    subModuleFromConfig: Boolean(options.purchaseDetailsSubModule),
  });

  const apiRows = await options.getSubModuleData(moduleName, recordId);

  logPurchaseWithoutOrderSubmodule("load_fetch_response", {
    recordId,
    moduleName,
    rowCount: Array.isArray(apiRows) ? apiRows.length : 0,
    sampleKeys:
      Array.isArray(apiRows) && apiRows[0] && typeof apiRows[0] === "object"
        ? Object.keys(apiRows[0] as object).slice(0, 12)
        : [],
  });

  if (!Array.isArray(apiRows) || apiRows.length === 0) {
    logPurchaseWithoutOrderSubmodule("load_abort_empty_api_rows", { recordId, moduleName });
    return false;
  }

  const mappedRows = options.mapRows(subModule, apiRows);
  if (!mappedRows.length) {
    logPurchaseWithoutOrderSubmodule("load_abort_empty_mapped_rows", {
      recordId,
      moduleName,
      apiRowCount: apiRows.length,
    });
    return false;
  }

  options.assignRows(
    formData as Record<string, any>,
    subModule,
    mappedRows,
    options.parentModuleName
  );
  applyPurchaseViewSubmoduleHydration(formData);

  logPurchaseWithoutOrderSubmodule("load_success", {
    recordId,
    moduleName,
    mappedRowCount: mappedRows.length,
    job_card_id_selected: formData.job_card_id_selected,
    bindKeys: PURCHASE_SUBMODULE_FORM_KEYS.map((key) => ({
      key,
      rowCount: Array.isArray(formData[key]) ? (formData[key] as unknown[]).length : formData[key],
    })),
  });
  return true;
}

export const PURCHASE_SUBMODULE_FORM_KEYS = [
  "jobcard_details",
  "purchase_details",
  "job_card_id_selected",
] as const;

const PUR139_SUBMODULE_LOG = "[PUR139 Submodule]";

/** Dev-only trace for screen 139 purchase_details submodule load (View / navigate). */
export function logPurchaseWithoutOrderSubmodule(
  step: string,
  payload?: Record<string, unknown>
): void {
  if (!import.meta.env.DEV) return;
  if (payload && Object.keys(payload).length > 0) {
    console.log(PUR139_SUBMODULE_LOG, step, payload);
  } else {
    console.log(PUR139_SUBMODULE_LOG, step);
  }
}
